import { FedopsLogger } from '@wix/fedops-logger';
import qs from 'qs';
import React from 'react';
import { RevalidateAppInstance } from '@wix/cashier-common/dist/src/types/AppInstance';
import {
  FramedModalMessage,
  FramedModalMessageInitPayload,
  FramedModalMessageType,
  CommonTransitiveMessageType,
  FramedModalQueryParams,
  FramedModalType,
} from '../../../types/FramedModal';
import { InteractionName } from '../../../types/InteractionName';
import { getDocumentWidth } from '../../../utils/documentWidth';
import { isInModal } from '../../../utils/isInModal';
import { messenger } from '../../../utils/messenger';
import { parseURL } from '../../../utils/parseURL';
import { wixSdkAdapter } from '../../../utils/wixSdkAdapter';
import {
  closeModalPath,
  framedModalPath,
  fullSizeWidthBreakpoint,
  maxReadyTimeout,
} from './constants';
import { FramedModalView } from './view';
import { win } from '../../../utils/win';

export interface FramedModalApi {
  sendTransitiveMessageToFrame<T = any>(payload: T): void;
}

export interface FramedModalProps {
  modalName: string;
  url: string;
  callbackPageOrigin?: string;
  title?: string;
  publicBaseUrl: string;
  locale: string;
  userId: string;
  widgetVersion?: string;
  width?: number | string;
  height?: number | string;
  isCustomModalVisible?: boolean;
  fedopsLogger: FedopsLogger<InteractionName>;

  onFrameMount?(): void;
  onReadyTimeout(): void;
  onApiInit?(api: FramedModalApi): void;
  onTransitiveMessageFromFrame(payload: any): void;
  onClose(): void;
  // in OOI env openModal should be passed via props
  openPlatformModal?(params: {
    url: string;
    width: string | number;
    height: string | number;
    theme: string;
    onClose(): void;
  }): void;
  revalidateAppInstance?: RevalidateAppInstance;
}

export class FramedModalController extends React.Component<FramedModalProps> {
  private modalWindow: Window = null;
  private readonly modalType: FramedModalType;
  private _isMounted: boolean = true;
  private isClosed: boolean = false;
  private readyTimeoutHandler = null;
  private startTime: number = Date.now();
  private readonly unListenMessage: () => void;

  constructor(props: FramedModalProps) {
    super(props);

    this.props.fedopsLogger.interactionStarted(
      InteractionName.FRAMED_MODAL_ALE
    );
    this.props.fedopsLogger.interactionStarted(
      InteractionName.FRAMED_MODAL_ALE_LOADER
    );

    this.modalType = this.getModalType();

    if (this.modalType === FramedModalType.Platform) {
      this.openPlatformModal();
    }

    this.unListenMessage = messenger.addListener(
      this.handlePostMessage,
      this.props.modalName
    );

    this.waitForModalReady();
  }

  private getModalType = (): FramedModalType => {
    // return FramedModalType.CustomFullSize;

    if (isInModal()) {
      return FramedModalType.CustomFullSize;
    }

    if (this.props.openPlatformModal || wixSdkAdapter.isConnected()) {
      return FramedModalType.Platform;
    }

    return getDocumentWidth() < fullSizeWidthBreakpoint
      ? FramedModalType.CustomFullSize
      : FramedModalType.Custom;
  };

  private handlePostMessage = (event: {
    origin: string;
    data?: FramedModalMessage;
    source: MessageEventSource;
  }) => {
    if (
      event.data &&
      event.data.id === this.props.modalName &&
      // with statics override to local version origins won't match
      (parseURL(this.props.publicBaseUrl)?.origin === event.origin ||
        process.env.NODE_ENV !== 'production')
    ) {
      switch (event.data.type) {
        case FramedModalMessageType.Ready:
          this.clearReadyTimeout();
          this.modalWindow = event.source as Window;

          this.sendMessageToModal(FramedModalMessageType.Init, {
            // url: this.props.publicBaseUrl + 'assets/pages/fake-pp-page.html',
            url: this.props.url,
            title: this.props.title,
            ...(this.props.callbackPageOrigin && {
              callbackPageOrigin: this.props.callbackPageOrigin,
            }),
          } as FramedModalMessageInitPayload);
          break;
        case FramedModalMessageType.Close:
          if (event.source === this.modalWindow) {
            this.isClosed = true;

            if (this.modalType === FramedModalType.Platform) {
              this.closePlatformModal();
            }

            this.props.onClose();
          }
          break;
        case FramedModalMessageType.FrameMount:
          if (event.source === this.modalWindow) {
            this.props.onFrameMount?.();
          }
          break;
        case FramedModalMessageType.TransitiveFromFrame:
          if (
            this._isMounted &&
            (event.source === this.modalWindow || this.modalWindow?.closed)
          ) {
            if (
              event.data.payload?.type ===
              CommonTransitiveMessageType.RevalidateAppInstance
            ) {
              const respondWithError = (error: Error) => {
                this.sendTransitiveMessageToFrame({
                  type: CommonTransitiveMessageType.RevalidateAppInstanceResult,
                  error,
                });
              };

              if (this.props.revalidateAppInstance) {
                this.props
                  .revalidateAppInstance()
                  .then((appInstance) => {
                    this.sendTransitiveMessageToFrame({
                      type: CommonTransitiveMessageType.RevalidateAppInstanceResult,
                      payload: { appInstance },
                    });
                  })
                  .catch(respondWithError);
              } else {
                respondWithError(
                  new Error(`"revalidateAppInstance" wasn't provided`)
                );
              }
            } else {
              this.props.onTransitiveMessageFromFrame(event.data.payload);
            }
          }
          break;
        default:
      }
    }
  };

  private waitForModalReady = () => {
    this.readyTimeoutHandler = win.setTimeout(() => {
      console.error(
        `Payments Widget :: FramedModal :: Ready wasn't received from Modal within max timeout`
      );

      if (this._isMounted) {
        this.props.onReadyTimeout();
      }

      this.readyTimeoutHandler = null;
    }, maxReadyTimeout);
  };

  private clearReadyTimeout = () => {
    if (this.readyTimeoutHandler !== null) {
      win.clearTimeout(this.readyTimeoutHandler);
      this.readyTimeoutHandler = null;
    }
  };

  private sendMessageToModal = (
    type: FramedModalMessageType,
    payload?: FramedModalMessageInitPayload
  ) => {
    if (!this.modalWindow) {
      console.error(
        `Payments Widget :: FramedModal :: modal window wasn't inited`
      );
      return;
    }

    const origin =
      process.env.NODE_ENV === 'production' // with statics override to local version origins won't match
        ? parseURL(this.props.publicBaseUrl).origin
        : '*';

    this.modalWindow.postMessage(
      {
        id: this.props.modalName,
        type,
        ...(payload && { payload }),
      } as FramedModalMessage,
      origin
    );
  };

  private get framedModalUrl() {
    return (
      this.props.publicBaseUrl +
      framedModalPath +
      '?' +
      qs.stringify({
        __modalParams: {
          id: this.props.modalName,
          modalType: this.modalType,
          widgetOrigin: document.location.origin,
          startTime: String(this.startTime),
          locale: this.props.locale,
          userId: this.props.userId,
          ...(this.props.widgetVersion && {
            widgetVersion: this.props.widgetVersion,
          }),
        },
      } as FramedModalQueryParams)
    );
  }

  private openPlatformModal = () => {
    const { width, height } = this.props;
    const openPlatformModal =
      this.props.openPlatformModal || wixSdkAdapter.openModal;

    openPlatformModal({
      url: this.framedModalUrl,
      width,
      height,
      theme: wixSdkAdapter.Wix.Theme.BARE,
      onClose: () => {
        this.modalWindow = null;
        this.isClosed = true;

        if (this._isMounted) {
          this.props.onClose();
        }
      },
    });
  };

  private closePlatformModal = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `Payments Widget :: FramedModal :: Wix.closeWindow() doesn't work if page was served from localhost (bug in WixSDK/Viewer). Click on the overlay to proceed`
      );
    }

    const openPlatformModal =
      this.props.openPlatformModal || wixSdkAdapter.openModal;
    // force close Platform Modal
    openPlatformModal({
      url: this.props.publicBaseUrl + closeModalPath,
      width: 0,
      height: 0,
      theme: wixSdkAdapter.Wix.Theme.BARE,
      onClose: () => null,
    });
  };

  private sendTransitiveMessageToFrame = (payload: any) => {
    this.sendMessageToModal(FramedModalMessageType.TransitiveToFrame, payload);
  };

  componentDidMount() {
    this.props.onApiInit?.({
      sendTransitiveMessageToFrame: this.sendTransitiveMessageToFrame,
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.unListenMessage();

    if (this.modalType === FramedModalType.Platform && !this.isClosed) {
      this.closePlatformModal();
    }

    this.modalWindow = null;
    this.clearReadyTimeout();
  }

  render() {
    if (this.modalType === FramedModalType.Platform) {
      return null;
    }

    let { width, height } = this.props;

    if (this.modalType === FramedModalType.CustomFullSize) {
      width = height = '100%';
    }

    return (
      <FramedModalView
        url={this.framedModalUrl}
        width={width}
        height={height}
        isVisible={this.props.isCustomModalVisible}
        onClose={this.props.onClose}
      />
    );
  }
}
